import { confidentialStorageIsAllowed } from "../lib/session_privacy";
import { Controller } from "@hotwired/stimulus";
import { serverTextValue } from "./server_text_value";

const BOTTOM_THRESHOLD_PX = 48;
const LOCAL_STORAGE_PREFIX = "elron:ticket-chat:";
const PERSIST_DEBOUNCE_MS = 200;

type HtmxAfterRequestEvent = CustomEvent<{
    elt?: Element;
    successful?: boolean;
    xhr?: XMLHttpRequest;
}>;

export default class extends Controller<HTMLFormElement> {
    static values = {
        attachmentPluralLabel: String,
        attachmentSingularLabel: String,
        currentUserLabel: String,
        sendingStatus: String,
        sentStatus: String,
        stopPendingLabel: String,
        stopResponseLabel: String,
    };

    declare readonly attachmentPluralLabelValue: string;
    declare readonly attachmentSingularLabelValue: string;
    declare readonly currentUserLabelValue: string;
    declare readonly sendingStatusValue: string;
    declare readonly sentStatusValue: string;
    declare readonly stopPendingLabelValue: string;
    declare readonly stopResponseLabelValue: string;
    declare readonly hasAttachmentPluralLabelValue: boolean;
    declare readonly hasAttachmentSingularLabelValue: boolean;
    declare readonly hasCurrentUserLabelValue: boolean;
    declare readonly hasSendingStatusValue: boolean;
    declare readonly hasSentStatusValue: boolean;
    declare readonly hasStopPendingLabelValue: boolean;
    declare readonly hasStopResponseLabelValue: boolean;

    private persistTimer: number | null = null;
    private persistInputListener: ((event: Event) => void) | null = null;

    private clearConfidentialDraft = (): void => {
        this.detachPersistenceListener();
        if (this.persistTimer !== null) window.clearTimeout(this.persistTimer);
        this.persistTimer = null;
        const textarea = this.textareaElement();
        if (textarea) textarea.value = "";
    };

    connect(): void {
        window.addEventListener("elron:session-ended", this.clearConfidentialDraft);
        this.restoreDraftFromLocalStorage();
        this.attachPersistenceListener();
    }

    disconnect(): void {
        window.removeEventListener("elron:session-ended", this.clearConfidentialDraft);
        this.detachPersistenceListener();
        if (this.persistTimer !== null) {
            window.clearTimeout(this.persistTimer);
            this.persistTimer = null;
            this.persistDraft(this.textareaElement()?.value || "");
        }
    }

    submitOnEnter(event: KeyboardEvent): void {
        if (!this.shouldSubmit(event)) {
            return;
        }

        event.preventDefault();
        this.element.requestSubmit();
    }

    submitByClick(event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLButtonElement) || trigger.disabled || trigger.dataset.ticketChatStopButton === "true") {
            return;
        }

        event.preventDefault();
        this.element.requestSubmit();
    }

    useSuggestion(event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        const textarea = this.textareaElement();
        const suggestion = trigger.dataset.ticketChatSuggestion;
        if (!textarea || !suggestion) {
            return;
        }

        event.preventDefault();
        textarea.value = suggestion;
        textarea.dispatchEvent(new Event("input", { bubbles: true }));
        textarea.focus();
        textarea.setSelectionRange(suggestion.length, suggestion.length);
        if (trigger.dataset.ticketChatSuggestionSubmit === "true") {
            this.element.requestSubmit();
        }
    }

    showInterrupted(event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLButtonElement) || trigger.dataset.ticketChatStopButton !== "true") {
            return;
        }

        this.cancelActiveStreams();
        this.appendInterruptedMessage();
        this.hideThinkingIndicator();
        this.markStopPending(trigger);
    }

    beforeRequest(event: Event): void {
        if (this.isStopRequestEvent(event)) {
            this.cancelActiveStreams();
            this.appendInterruptedMessage();
            this.hideThinkingIndicator();
            this.markStopPending((event as HtmxAfterRequestEvent).detail?.elt);
            return;
        }

        this.composerTrace("composerSubmit", { contentChars: this.pendingUserMessageContent().length });
        this.appendPendingUserMessage();
    }

    afterRequest(event: Event): void {
        if (this.isStopRequestEvent(event)) {
            if (this.wasSuccessfulNoContent(event)) {
                this.confirmPendingInterruptedMessage();
                return;
            }

            this.removePendingInterruptedMessage();
            this.restoreThinkingIndicator();
            this.resetStopPending((event as HtmxAfterRequestEvent).detail?.elt);
            this.resumeActiveStreams();
            return;
        }

        const detail = (event as HtmxAfterRequestEvent).detail || {};
        this.composerTrace("composerResponse", {
            httpStatus: detail.xhr?.status,
            successful: detail.successful !== false,
        });
        if (this.wasSuccessfulNoContent(event)) {
            // Promote the optimistic pending bubble to the real one BEFORE the
            // next morphdom swap can mismatch the IDs and trigger a flicker.
            const realId = detail.xhr?.getResponseHeader?.("X-Elron-Message-Id") || "";
            this.confirmPendingUserMessage(realId.replace(/^"|"$/g, ""));
            this.clearPersistedDraft();
            this.requestChatRefresh("message-created");
            return;
        }

        this.removePendingUserMessage();
    }

    // ---- localStorage persistence ---------------------------------------

    private restoreDraftFromLocalStorage(): void {
        if (!confidentialStorageIsAllowed()) return;
        const key = this.localStorageKey();
        const textarea = this.textareaElement();
        if (!key || !textarea) return;
        let stored: string | null = null;
        try {
            stored = window.localStorage.getItem(key);
        } catch (err) {
            // localStorage can throw in private-browsing / quota errors.
            console.warn("[ticket-chat-composer] localStorage.getItem failed", err);
            return;
        }
        // No localStorage entry → user hasn't typed on this ticket yet.
        // Trust whatever the server pre-filled (e.g. an in-flight voice
        // transcript that the sync POST already wrote into the textarea on
        // first paint). The first input event will start populating
        // localStorage shortly after.
        if (stored === null) return;

        // localStorage exists → user has previously interacted with this
        // textarea. Their local state is the source of truth: override
        // whatever the server pre-rendered, even if it's a (stale) voice
        // transcript from a completed draft they already cleared once.
        if (textarea.value !== stored) {
            textarea.value = stored;
            textarea.dispatchEvent(new Event("input", { bubbles: true }));
        }
    }

    private attachPersistenceListener(): void {
        const textarea = this.textareaElement();
        if (!textarea) return;
        const listener = () => this.schedulePersist();
        textarea.addEventListener("input", listener);
        this.persistInputListener = listener;
    }

    private detachPersistenceListener(): void {
        if (!this.persistInputListener) return;
        const textarea = this.textareaElement();
        textarea?.removeEventListener("input", this.persistInputListener);
        this.persistInputListener = null;
    }

    private schedulePersist(): void {
        if (this.persistTimer !== null) {
            window.clearTimeout(this.persistTimer);
        }
        this.persistTimer = window.setTimeout(() => {
            this.persistTimer = null;
            this.persistDraft(this.textareaElement()?.value || "");
        }, PERSIST_DEBOUNCE_MS);
    }

    private persistDraft(value: string): void {
        if (!confidentialStorageIsAllowed()) return;
        const key = this.localStorageKey();
        if (!key) return;
        try {
            // Store the value verbatim (including empty string). An empty
            // entry is meaningful: it records the user's "I cleared this
            // textarea" intent, so the server-rendered fallback (e.g. a
            // completed voice transcript that hasn't been consumed yet)
            // doesn't resurrect content the user already dismissed.
            window.localStorage.setItem(key, value);
        } catch (err) {
            console.warn("[ticket-chat-composer] localStorage.setItem failed", err);
        }
    }

    private clearPersistedDraft(): void {
        if (this.persistTimer !== null) {
            window.clearTimeout(this.persistTimer);
            this.persistTimer = null;
        }

        const key = this.localStorageKey();
        if (!key) return;
        try {
            window.localStorage.removeItem(key);
        } catch (err) {
            console.warn("[ticket-chat-composer] localStorage.removeItem failed", err);
        }
    }

    private localStorageKey(): string | null {
        const fragment = this.element.closest<HTMLElement>("#tickets-chat-fragment");
        const ticketId = fragment?.dataset.ticketId;
        if (!ticketId) return null;
        return `${LOCAL_STORAGE_PREFIX}${ticketId}`;
    }

    private textareaElement(): HTMLTextAreaElement | null {
        return this.element.querySelector<HTMLTextAreaElement>('textarea[name="content"]');
    }

    private requestChatRefresh(reason: string): void {
        const registration = document.getElementById("tickets-chat-auto-refresh-registration");
        if (!registration) {
            this.composerTrace("composerRefreshSkipped", { reason, cause: "missingRegistration" });
            return;
        }

        this.composerTrace("composerRefreshRequested", { reason });
        registration.dispatchEvent(new CustomEvent("ticket-chat-refresh", { bubbles: true }));
    }

    private composerTrace(kind: string, fields: Record<string, unknown>): void {
        const traceEnabled = window.__streamTrace === true ||
            new URLSearchParams(window.location.search).get("streamTrace") === "1";
        if (!traceEnabled) return;
        const ts = Math.round(performance.now());
        const event: StreamTraceEvent = { ts, kind, ...fields };
        window.__streamTraceLog ??= [];
        window.__streamTraceLog.push(event);
        // eslint-disable-next-line no-console
        console.debug("[composer]", kind, event);
    }

    private shouldSubmit(event: Event): event is KeyboardEvent {
        return (
            event instanceof KeyboardEvent &&
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.altKey &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.isComposing &&
            event.keyCode !== 229 &&
            this.element instanceof HTMLFormElement
        );
    }

    private appendInterruptedMessage(): void {
        const panel = this.element.closest<HTMLElement>("#tickets-chat-fragment");
        const messageList = panel?.querySelector<HTMLElement>('[data-testid="ticket-chat-messages"]');
        const message = panel?.dataset.ticketChatInterruptionMessage;
        if (!panel || !messageList || !message || messageList.querySelector('[data-ticket-chat-interrupt-pending="true"]')) {
            return;
        }

        const scrollContainer = panel.querySelector<HTMLElement>("#tickets-chat-scroll");
        const shouldFollowBottom = this.isNearBottom(scrollContainer);

        const row = document.createElement("div");
        row.className = "flex w-full items-center gap-3 py-1 text-xs font-medium text-muted-foreground";
        row.dataset.ticketChatInterruptPending = "true";
        row.setAttribute("data-testid", "ticket-chat-message");
        row.setAttribute("data-message-role", "system");
        row.setAttribute("data-message-status", "pending");

        const leftLine = this.buildInterruptionLine("left");
        const label = document.createElement("span");
        label.className = "shrink-0";
        label.textContent = message;
        const rightLine = this.buildInterruptionLine("right");

        row.append(leftLine, label, rightLine);
        messageList.append(row);

        if (scrollContainer && shouldFollowBottom) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
    }

    private appendPendingUserMessage(): void {
        const panel = this.element.closest<HTMLElement>("#tickets-chat-fragment");
        const messageList = panel?.querySelector<HTMLElement>('[data-testid="ticket-chat-messages"]');
        const content = this.pendingUserMessageContent();
        if (!panel || !messageList || !content || messageList.querySelector('[data-ticket-chat-user-pending="true"]')) {
            return;
        }

        const scrollContainer = panel.querySelector<HTMLElement>("#tickets-chat-scroll");
        const row = document.createElement("div");
        row.className = "flex justify-end";
        row.dataset.ticketChatUserPending = "true";
        row.setAttribute("data-testid", "ticket-chat-message");
        row.setAttribute("data-message-id", `pending-${Date.now()}`);
        row.setAttribute("data-message-role", "user");
        row.setAttribute("data-message-status", "queued");

        const wrap = document.createElement("div");
        wrap.className = "max-w-[92%] min-w-0 sm:max-w-[85%]";
        const meta = document.createElement("div");
        meta.className = "mb-1 flex items-center justify-end gap-1.5 text-[11px] font-medium text-muted-foreground";
        const sender = document.createElement("span");
        sender.textContent = this.currentUserLabel();
        const status = document.createElement("span");
        status.className = "rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground";
        status.textContent = this.sendingStatus();
        const bubble = document.createElement("div");
        bubble.className = "ml-auto w-fit max-w-full rounded-2xl elron-grad-dark px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line break-words text-white";
        bubble.textContent = content;

        meta.append(sender, status);
        wrap.append(meta, bubble);
        row.append(wrap);
        messageList.append(row);

        if (scrollContainer) {
            // Always notify the scroll controller — even if the user is
            // currently scrolled up. Submitting a new message is an explicit
            // user intent to move forward in the conversation, so we want to
            // jump to the bottom and anchor at ~30% regardless of where they
            // were reading. The scroll controller handles the actual scroll.
            scrollContainer.dispatchEvent(
                new CustomEvent("ticket-chat:user-anchor", {
                    bubbles: false,
                    detail: { messageNode: row },
                }),
            );
        }
    }

    private pendingUserMessageContent(): string {
        const textInput = this.element.querySelector<HTMLTextAreaElement>('textarea[name="content"]');
        const text = textInput?.value.trim() || "";
        if (text) {
            return text;
        }

        const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="attachments"]');
        const attachmentCount = fileInput?.files?.length || 0;
        if (attachmentCount <= 0) {
            return "";
        }

        const attachmentLabel = attachmentCount > 1 ? this.attachmentPluralLabel() : this.attachmentSingularLabel();
        return `${attachmentCount} ${attachmentLabel}`;
    }

    private confirmPendingUserMessage(realMessageId: string = ""): void {
        const message = this.pendingUserMessage();
        if (!message) {
            return;
        }

        delete message.dataset.ticketChatUserPending;
        message.setAttribute("data-message-status", "queued");
        message.querySelector("span:last-child")?.replaceChildren(this.sentStatus());
        // Adopt the server-assigned id so the next morphdom swap matches the
        // pending bubble in place (no flicker / pop-in).
        if (realMessageId) {
            message.setAttribute("data-message-id", realMessageId);
        }
    }

    private removePendingUserMessage(): void {
        this.pendingUserMessage()?.remove();
    }

    private pendingUserMessage(): HTMLElement | null {
        return this.element
            .closest<HTMLElement>("#tickets-chat-fragment")
            ?.querySelector<HTMLElement>('[data-ticket-chat-user-pending="true"]') || null;
    }

    private buildInterruptionLine(side: "left" | "right"): HTMLDivElement {
        const line = document.createElement("div");
        const gradientClass = side === "left"
            ? "bg-gradient-to-r from-transparent via-gray-300 to-gray-300"
            : "bg-gradient-to-r from-gray-300 via-gray-300 to-transparent";
        line.className = `h-px min-w-0 flex-1 ${gradientClass}`;
        line.dataset.side = side;
        line.setAttribute("data-testid", "ticket-chat-interruption-line");
        return line;
    }

    private confirmPendingInterruptedMessage(): void {
        const message = this.pendingInterruptedMessage();
        if (!message) {
            return;
        }

        delete message.dataset.ticketChatInterruptPending;
        message.setAttribute("data-message-status", "sent");
    }

    private removePendingInterruptedMessage(): void {
        this.pendingInterruptedMessage()?.remove();
    }

    private pendingInterruptedMessage(): HTMLElement | null {
        return this.element
            .closest<HTMLElement>("#tickets-chat-fragment")
            ?.querySelector<HTMLElement>('[data-ticket-chat-interrupt-pending="true"]') || null;
    }

    private cancelActiveStreams(): void {
        window.dispatchEvent(new CustomEvent("ticket-chat:cancel-active-streams"));
    }

    private resumeActiveStreams(): void {
        window.dispatchEvent(new CustomEvent("ticket-chat:resume-active-streams"));
    }

    private hideThinkingIndicator(): void {
        const indicator = this.thinkingIndicator();
        if (!indicator || indicator.hidden) {
            return;
        }

        indicator.hidden = true;
        indicator.dataset.ticketChatThinkingHiddenByStop = "true";
    }

    private restoreThinkingIndicator(): void {
        const indicator = this.element
            .closest<HTMLElement>("#tickets-chat-fragment")
            ?.querySelector<HTMLElement>('[data-ticket-chat-thinking-hidden-by-stop="true"]');
        if (!indicator) {
            return;
        }

        indicator.hidden = false;
        delete indicator.dataset.ticketChatThinkingHiddenByStop;
    }

    private thinkingIndicator(): HTMLElement | null {
        return this.element
            .closest<HTMLElement>("#tickets-chat-fragment")
            ?.querySelector<HTMLElement>('[data-testid="ticket-chat-thinking-indicator"]') || null;
    }

    private markStopPending(trigger: Element | undefined): void {
        if (!(trigger instanceof HTMLButtonElement)) {
            return;
        }

        trigger.dataset.ticketChatStopPending = "true";
        trigger.classList.add("animate-pulse", "opacity-70");
        trigger.dataset.tooltip = this.stopPendingLabel();
        trigger.setAttribute("aria-label", this.stopPendingLabel());
    }

    private resetStopPending(trigger: Element | undefined): void {
        if (!(trigger instanceof HTMLButtonElement)) {
            return;
        }

        delete trigger.dataset.ticketChatStopPending;
        trigger.classList.remove("animate-pulse", "opacity-70");
        trigger.dataset.tooltip = this.stopResponseLabel();
        trigger.setAttribute("aria-label", this.stopResponseLabel());
    }

    private isStopRequestEvent(event: Event): boolean {
        const detail = (event as HtmxAfterRequestEvent).detail || {};
        const trigger = detail.elt || event.target;

        return trigger instanceof HTMLElement && trigger.dataset.ticketChatStopButton === "true";
    }

    private wasSuccessfulNoContent(event: Event): boolean {
        const detail = (event as HtmxAfterRequestEvent).detail || {};
        const status = detail.xhr?.status;

        return status === 204 && detail.successful !== false;
    }

    private isNearBottom(element: HTMLElement | null): boolean {
        if (!element) {
            return true;
        }

        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        return distanceFromBottom <= BOTTOM_THRESHOLD_PX;
    }

    private attachmentPluralLabel(): string {
        return this.textValue(
            this.hasAttachmentPluralLabelValue,
            this.attachmentPluralLabelValue,
            "attachmentPluralLabel",
            "attachments",
        );
    }

    private attachmentSingularLabel(): string {
        return this.textValue(
            this.hasAttachmentSingularLabelValue,
            this.attachmentSingularLabelValue,
            "attachmentSingularLabel",
            "attachment",
        );
    }

    private currentUserLabel(): string {
        return this.textValue(this.hasCurrentUserLabelValue, this.currentUserLabelValue, "currentUserLabel", "You");
    }

    private sendingStatus(): string {
        return this.textValue(this.hasSendingStatusValue, this.sendingStatusValue, "sendingStatus", "Sending...");
    }

    private sentStatus(): string {
        return this.textValue(this.hasSentStatusValue, this.sentStatusValue, "sentStatus", "Sent");
    }

    private stopPendingLabel(): string {
        return this.textValue(this.hasStopPendingLabelValue, this.stopPendingLabelValue, "stopPendingLabel", "Stopping...");
    }

    private stopResponseLabel(): string {
        return this.textValue(this.hasStopResponseLabelValue, this.stopResponseLabelValue, "stopResponseLabel", "Stop response");
    }

    private textValue(hasValue: boolean, value: string, name: string, fallback: string): string {
        return serverTextValue({
            controller: this.identifier,
            fallback,
            hasValue,
            name,
            value,
        });
    }
}
